import { ReactSortable } from 'react-sortablejs';
import {useSafeState} from "ahooks";
import type { IColumnInfo } from '@dbl/core/dataSource';
import type { ISortableOption } from '@/pages/datalab/components/SortableDrag/typeDefine';
import { checkNullString } from '@/pages/datalab/utils/utils';
import { Dropdown } from 'tea-component';
import React, {useMemo, useState} from 'react';
import { initDragStatus } from '@/pages/datalab/components/SortableDrag/utils';
import { createMetricSortablePersist } from '@/pages/datalab/components/SortableDrag/utils/metricSortablePersist';
import { TDispatchFun, TItemOnChange, SortableItems } from '@/pages/datalab/components/SortableDrag/configration';
import { MetricsSelect } from '@/pages/datalab/components/SortableDrag/select/type_select';
import SvgIcon from '@/pages/home/components/SvgIcon';
import { DropdownIcon } from '@/pages/datalab/components/SortableDrag/Sortable/dropdownIcon';
import { SortableAffix } from '@/pages/datalab/components/SortableDrag/Sortable/sortableAffix';
import {
  duplicateNameVerification,
  RenameWrapper
} from "@/pages/datalab/components/SortableDrag/FieldEditComponents";
import type { IGetTableInfoDto } from '@dbl/services/dataSource';
import { CustomColumn } from '@/pages/datalab/pages/Multidimensional/Analysis/interface';
import { ChartType } from '@dbl/core/chart';
import { ChartTypeInfos } from '@dbl/core/chartEditor';
import {checkConditions} from "@/pages/datalab/pages/Multidimensional/utils";
import {ITableJoinRelation} from "@/pages/datalab/pages/Multidimensional/Analysis/components/HeaderData/interface";
export interface MetricSortableProps {
  sortableKey: string;
  sortableList: IColumnInfo[];
  sortableOption: ISortableOption;
  dispatchFun: TDispatchFun;
  isEn: boolean;
  dataTables: IGetTableInfoDto[];
  chartType: string;
  showCustomColumnModel: (column: IColumnInfo) => void;
  dataTableJoinRelations: ITableJoinRelation[];
  saveColumnInfos: any;
  customColumns?: CustomColumn[];
}
export const MetricSortable = (props: MetricSortableProps) => {
  const { sortableKey, sortableList, sortableOption, dispatchFun, isEn, dataTables, chartType, showCustomColumnModel, dataTableJoinRelations, saveColumnInfos, customColumns } = props;
  const { dragState, dragMoveHandle } = sortableOption;
  const [currentDragItem, setCurrentDragItem] = useState<IColumnInfo>();
  const [renameSelectComp, setRenameSelectComp] = useSafeState<string>('');
  const metricPersist = useMemo(
    () =>
      createMetricSortablePersist({
        sortableKey,
        chartType,
        dispatchFun,
        saveColumnInfos,
      }),
    [sortableKey, chartType, dispatchFun, saveColumnInfos],
  );
  
  const {
    errorDrag: { [sortableKey]: errorDragType },
    currentDrag,
  } = dragState;

  const itemOnChange: TItemOnChange = (columnInfo) => {
    const index = sortableList.findIndex((item) => item.id === columnInfo.id && item.table_code === columnInfo.table_code);

    if (index >= 0) {
      sortableList[index] = {
        ...sortableList[index],
        ...columnInfo,
      };
      dispatchFun('save', { [sortableKey]: sortableList.filter((item) => !checkNullString(item.id)), forceAssignEmptyHashAnalysis: true });
    }
  };
  
  const updateHandle = (column: IColumnInfo, updateColumn: any) => {
    dispatchFun(
      'updateTableField',
      {
        table_id: column.table_id,
        isEn,
        lan: isEn ? 'en' : 'zh',
        column,
        updateColumn,
      },
    );
  };
  
  const {dragSuffixText, disabledDrag, disabledError} = useMemo(() => {
    const {metric} = ChartTypeInfos[chartType] || {};
    const {isCheckSuccess, info, disabledError} = checkConditions(metric, sortableList.slice(0, -2), isEn);
    const disabledDrag = (metric?.operator === '===' || metric?.operator === '<=') && sortableList.length - 2 === metric.count;
    return {
      dragSuffixText: isCheckSuccess ? '' : info,
      disabledDrag,
      disabledError: disabledDrag && currentDrag ? disabledError : '',
    };
  }, [chartType, isEn, sortableList, currentDrag]);

  const batchAddConfig = {
    enabled: true,
    targetKey: sortableKey as 'columnMetrics',
    dataTables,
    customColumns: customColumns || [],
    existingColumns: sortableList.filter(item => !checkNullString(item.id)),
    dispatchFun,
    chartType,
    isEn,
    saveColumnInfos,
    metricPersist,
  };

  return (
    <ReactSortable
      // disabled={disabledDrag}
      group="groupName"
      animation={100}
      filter={'.data-label-disabled'}
      handle={'.data-label-tips'}
      ghostClass="data-label-tips-green"
      delay={50}
      forceFallback={true}
      list={sortableList}
      className={`form-data-label-tips ${sortableKey}-${SortableItems} ${
        dragState.currentDrag === sortableKey ? 'metrics-acitve' : ''
      }`}
      disabled={!!renameSelectComp}
      setList={(list) => {
        // 组合图和词云图限制两个统计值（fix：修正两个指标顺序无法对换）
        const arithmeticOperation =
          (chartType === ChartType.MixedChart || chartType === ChartType.WordCloud)
            ? list?.length >= 5
            : list?.length > 2;
        // disabledDrag 判断统计指标是否超出1个的限制;  如果超出了, list.length为2的时候, 说明是将统计指标移除, 允许更新
        if (dragState.currentDrag === sortableKey && !checkNullString(errorDragType) || (disabledDrag && arithmeticOperation)) {
          return null;
        }
        if (JSON.stringify(sortableList) !== JSON.stringify(list)) {
          metricPersist.saveList(list);
        }
        
      }}
      onMove={(evt: any, originalEvent: any) => {
        if (currentDragItem) {
          return dragMoveHandle(evt, originalEvent, dispatchFun, currentDragItem);
        }
        return false;
      }}
      onEnd={() => {
        initDragStatus(dispatchFun);
      }}
      onStart={(evt) => {
        setCurrentDragItem(sortableList[evt.oldIndex || 0]);
      }}
    >
      {sortableList.map((item, index) =>
        item.id ? (<RenameWrapper
          setRenameSelectComp={setRenameSelectComp}
          key={index}
          className={'green'}
          isEn={isEn}
          oldName={item.column_display}
          changeRename={(name: string) => {
            const columns = dataTables?.find(({table_id}) => table_id === item.table_id)?.column_list || [];
            if (duplicateNameVerification(columns, name, isEn)) {
              return isEn ? 'Duplicate naming' : '重复命名';
            }
            updateHandle(item, {display_name: name});
          }}
          renderItem={(ref: any, clickRename: Function) => (<div ref={ref} className={`data-label-tips green`} style={{ display: 'flex' }} key={item.id + item.table_code}>
            <Dropdown
              matchButtonWidth
              boxClassName={'dbl-multi-metric-drop-box'}
              // className={item.is_aggregate ? 'custom-aggregate' : ''}
              // disabled={item.is_aggregate}
              updateOnChildrenChange={true}
              placementOffset={[-7, 5]}
              clickClose={false}
              button={
                <div className="dt-multiple-dropdown-group">
                  <div className="dt-multiple-dropdown-text">
                    {`${item.column_display || item.column_name}: ${
                      item.column_text ? item.column_text : '-'
                    }`}
                  </div>
                  <DropdownIcon />
                </div>
              }
            >
              {
                (close) => (<MetricsSelect item={item} itemOnChange={itemOnChange} chartType={chartType} clickRename={() => {clickRename();setRenameSelectComp(isEn ? item.column_display_en : item.column_display)}} close={close} updateHandle={updateHandle} dispatchFun={dispatchFun} showCustomColumnModel={showCustomColumnModel} dataTables={dataTables} dataTableJoinRelations={dataTableJoinRelations}/>)
              }
            </Dropdown>
            <div
              className="close-icon"
              onClick={() => {
                metricPersist.removeItems(sortableList, [item]);
              }}
            >
              <i className="selectOther-flex-icon">
                <SvgIcon type="clonse" />
              </i>
            </div>
          </div>)}/>) : (
          <SortableAffix key={index} item={item} dragState={dragState} sortableKey={sortableKey} dragSuffixText={dragSuffixText} disabledDrag={disabledDrag} disabledError={disabledError} batchAddConfig={batchAddConfig}/>
        ),
      )}
    </ReactSortable>
  );
};
